/**
 * The online half of the menu: the `RoomClient` and its transport, the host's
 * create-room form, and the single handoff from the lobby to the table.
 *
 * **Both ways in land here.** A room that fills by itself and a host pressing
 * "Start now" both arrive as the same thing — `GAME_START`, i.e. `RoomClient`
 * reaching `ready` — so the screens never navigate to the table themselves.
 * They only send; this class watches the room and mounts `GameScene` once.
 *
 * Kept in `scenes/` with the rest of the composition glue so the screens stay
 * DOM-only and `main.ts` keeps owning nothing but the dependency graph.
 */

import { ConnectionManager, RoomClient, SessionStore, IDENTITY_ROOM_GRACE_WINDOW_MS, type LobbyInfo, type SessionEntry } from '@bn-games/room-client';
import { getWsTicket } from '@bn-games/auth-client';
import { isDiscordActivity, type DiscordActivityContext } from '@bn-games/discord-activity';
import { DEFAULT_PLAYER_NAME, createRoomArgs, defaultRoomForm, type OnlineRoomForm } from '../network/roomOptions.js';
import { DISCORD_HUMAN_SEATS, HOST_SEAT, SESSION_STORAGE_KEY, resolveServerUrl } from './onlineHostConfig.js';
import { performRoomResume } from './onlineResume.js';
import type { ClientMessage, RulesetId, ServerMessage } from '../network/protocol.js';
import type { OnlineMenuApi, Scene } from '../ui/types.js';
import type { GameScene } from './GameScene.js';

export interface OnlineMenuHostDeps {
  readonly gameScene: GameScene;
  readonly navigate: (scene: Scene) => void;
  /** Seeds the form's ruleset from the player's solo preference. */
  readonly initialRulesetId: RulesetId;
  /** True while a game is already on screen — see `enterTable`. */
  readonly isPlaying: () => boolean;
}

export class OnlineMenuHost implements OnlineMenuApi {
  private readonly deps: OnlineMenuHostDeps;
  private readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  private readonly room: RoomClient<ClientMessage, ServerMessage>;
  private readonly sessionStore: SessionStore;
  private form: OnlineRoomForm;
  private host = false;
  /** Discord path only — see `joinDiscordInstance`. */
  private hostFromSeat = false;
  private atTable = false;
  /** Set only while a resumeRoom() is seated-but-waiting — see disconnect(). */
  private pendingResumeAbandon: (() => void) | null = null;

  constructor(deps: OnlineMenuHostDeps) {
    this.deps = deps;
    this.form = defaultRoomForm(deps.initialRulesetId);
    this.connection = new ConnectionManager<ClientMessage, ServerMessage>({
      serverUrl: resolveServerUrl(),
    });
    // Resolve the WS ticket at connect time, never at screen-mount time: the
    // ticket's TTL is 60s and the online screens routinely sit open longer,
    // which silently seats a signed-in player anonymously for the life of the
    // room. Discord keeps setTicket() — its ticket comes from the activity
    // handshake and cannot be re-minted here.
    if (!isDiscordActivity()) this.connection.setTicketProvider(getWsTicket);
    this.sessionStore = new SessionStore(SESSION_STORAGE_KEY, IDENTITY_ROOM_GRACE_WINDOW_MS);
    this.room = new RoomClient<ClientMessage, ServerMessage>(this.connection, this.sessionStore);
    this.room.onUpdate((info) => this.onRoomInfo(info));
  }

  getSavedSession(): SessionEntry | null {
    return this.sessionStore.load();
  }

  /** Resumes a held seat by room code — invite-link hash (T7) and the
   *  stored-session menu button (T8) both land here. Flow lives in
   *  `onlineResume.ts` (200-line cap); this is a thin call-through. */
  resumeRoom(roomCode: string, legacyToken?: string): Promise<void> {
    const deps = {
      room: this.room,
      sessionStore: this.sessionStore,
      navigate: this.deps.navigate,
      setHost: (host: boolean): void => { this.host = host; },
      setPendingAbandon: (abandon: (() => void) | null): void => {
        this.pendingResumeAbandon = abandon;
      },
    };
    return performRoomResume(deps, roomCode, legacyToken);
  }

  getRoomInfo(): LobbyInfo {
    return this.room.getInfo();
  }

  onRoomUpdate(listener: () => void): () => void {
    return this.room.onUpdate(() => listener());
  }

  getForm(): OnlineRoomForm {
    return this.form;
  }

  setForm(partial: Partial<OnlineRoomForm>): void {
    this.form = { ...this.form, ...partial };
  }

  createRoom(): void {
    const args = createRoomArgs(this.form);
    this.host = true;
    // `preferredSeat` stays undefined on purpose — see `network/roomOptions.ts`.
    this.room.createRoom(args.playerName, args.variant, undefined, args.seatCount, args.verifiable);
  }

  /** The join-by-code entry point; its screen is the plan's T16. */
  joinRoom(code: string): void {
    this.host = false;
    this.room.joinRoom(code, DEFAULT_PLAYER_NAME);
  }

  /**
   * The Discord entry point. Everyone who launched the activity in the same
   * channel shares one instance id, so it doubles as the room key — no code to
   * exchange, and the room is sized per `DISCORD_HUMAN_SEATS`.
   *
   * `host` can't be asserted the way `createRoom`/`joinRoom` assert it: with
   * `joinOrCreate` we only learn which of the two we did once the server
   * answers. It is read off the seat instead — `START_GAME` is accepted from
   * seat 0 alone, so that is who the Start button belongs to.
   */
  joinDiscordInstance(ctx: DiscordActivityContext): void {
    this.hostFromSeat = true;
    this.connection.setTicket(ctx.ticket);
    // `preferredSeat` stays undefined on purpose — see `network/roomOptions.ts`.
    this.room.joinOrCreate(ctx.instanceId, {
      playerName: ctx.displayName,
      variant: this.form.rulesetId,
      seatCount: DISCORD_HUMAN_SEATS,
      verifiable: this.form.verifiable,
    });
  }

  startGame(): void {
    this.connection.send({ type: 'START_GAME' });
  }

  isHost(): boolean {
    return this.host;
  }

  leaveRoom(): void {
    this.disconnect();
  }

  /** True once `GAME_START` has put us at a table, until we leave it. Lets a
   *  caller drop the room only when there is one — `disconnect` also clears the
   *  saved session, so calling it on a solo exit would throw away a rejoinable
   *  online seat the player still holds. */
  isAtTable(): boolean {
    return this.atTable;
  }

  /** Drop the room without navigating. `main.ts` calls this whenever the
   *  game scene is left by any route (the overlay's Leave, or Escape), so a
   *  stale socket can never outlive the table. Idempotent. */
  disconnect(): void {
    this.pendingResumeAbandon?.();
    this.pendingResumeAbandon = null;
    this.atTable = false;
    this.host = false;
    this.hostFromSeat = false;
    this.room.cancel();
  }

  private onRoomInfo(info: LobbyInfo): void {
    // Create/join already know; only the Discord path reads host off the room.
    if (this.hostFromSeat && info.localSeat !== null) {
      this.host = info.localSeat === HOST_SEAT;
    }
    if (info.state !== 'ready' || this.atTable) return;
    // Not yet at the table but a game is already on screen means free play:
    // inside Discord the instance room stays open while the player waits, so
    // mounting the table here would replace the solo game they started to pass
    // the time. They reach the table from the lobby instead.
    if (this.deps.isPlaying()) return;
    this.enterTable();
  }

  private enterTable(): void {
    this.atTable = true;
    // Navigate first so the canvas is visible before the scene starts its rAF
    // loop — the same handoff order free play uses.
    this.deps.navigate('game');
    this.deps.gameScene.mount({
      mode: 'online',
      room: this.room,
      // The overlay already surfaces the terminal and there is no history to
      // record yet, so the menu layer has nothing left to do with it.
      onGameOver: () => {},
      onLeaveTable: () => this.leaveTable(),
    });
  }

  private leaveTable(): void {
    this.disconnect();
    this.deps.navigate('menu');
  }
}
